const defaultStrings = {
  brand: {
    name: 'AlgoForge',
    organization: '',
    logoUrl: '',
    logoDarkUrl: '',
    faviconUrl: '',
  },

  features: {
    forgotPassword: true,
  },

  common: {
    retry: 'Try again',
    loading: 'Loading…',
    genericError: 'Something went wrong. Please try again.',
    networkError: 'Can’t reach the server. Check your connection and try again.',
    notFoundTitle: 'Page not found',
    notFoundText: 'The page you’re looking for doesn’t exist or has moved.',
    goToProblems: 'Go to problems',
    copy: 'Copy',
    copied: 'Copied',
    cancel: 'Cancel',
    dismiss: 'Dismiss',
    previous: 'Previous',
    next: 'Next',
    units: {
      days: 'days',
      hours: 'hrs',
      minutes: 'min',
      seconds: 'sec',
    },
  },

  nav: {
    primary: 'Main',
    skipToContent: 'Skip to content',
    problems: 'Problems',
    contests: 'Contests',
    blog: 'Blog',
    tracker: 'Tracker',
    courses: 'Courses',
    profile: 'Profile',
    signOut: 'Sign out',
    switchToLight: 'Switch to light theme',
    switchToDark: 'Switch to dark theme',
    accountMenu: 'Account menu',
    streak: { one: '{count}-day streak', other: '{count}-day streak' },
    streakAtRisk: {
      one: '{count}-day streak. Solve today’s problem to keep it going.',
      other: '{count}-day streak. Solve today’s problem to keep it going.',
    },
    live: 'Live',
    liveBanner: '{name} is live',
    liveBannerAction: 'Enter contest',
  },

  palette: {
    label: 'Command menu',
    trigger: 'Search or jump to…',
    placeholder: 'Search problems, contests and actions',
    escape: 'Esc',
    empty: 'Nothing matches “{query}”.',
    hintNavigate: 'to move',
    hintOpen: 'to open',
    today: 'Open today’s problem: {title}',
    random: 'Pick a random unsolved problem',
    writePost: 'Write a blog post',
    theme: {
      light: 'Switch to light theme',
      dark: 'Switch to dark theme',
      system: 'Match the system theme',
    },
    groups: {
      pages: 'Go to',
      actions: 'Actions',
      problems: 'Problems',
      contests: 'Contests',
    },
  },

  auth: {
    showcase: {
      headline: 'Code every day.',
      headlineAccent: 'Compete when it counts.',
      lede: 'Solve problems in a real editor, get verdicts from a judge running hidden tests, keep your daily streak alive and climb the contest standings.',
      writing: 'Writing a solution…',
      features: [
        { title: 'A real judge', text: 'Hidden tests in C++, Java, Python and JavaScript.' },
        { title: 'A daily problem', text: 'A fresh challenge every day and a streak to protect.' },
        { title: 'Live contests', text: 'Timed rounds with penalties and live standings.' },
      ],
    },
    signInTitle: 'Welcome back',
    signInSubtitle: 'Sign in to {brand} to pick up where you left off.',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    accountsManaged: 'Accounts are created by your institution. Ask your administrator if you don’t have one.',
    accountsManagedBy: 'Accounts are created by {organization}. Ask your administrator if you don’t have one.',
    signupPrompt: 'New here?',
    signupLink: 'Create an account',
    signupTitle: 'Create your account',
    signupSubtitle: 'Join {brand} to practise problems and take contests.',
    username: 'Username',
    usernameHint: 'Shown on contest standings. Letters, numbers, dots and underscores.',
    createAccount: 'Create account',
    creatingAccount: 'Creating account…',
    haveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    signupSentTitle: 'Check your email',
    signupSentText: 'We sent a verification link to {email}. Open it to activate your account, then sign in.',
    forgotTitle: 'Reset your password',
    forgotText: 'Enter the email you sign in with and we’ll send you a link to set a new password.',
    sendLink: 'Send reset link',
    sendingLink: 'Sending…',
    forgotSentTitle: 'Check your email',
    forgotSentText: 'If an account exists for {email}, a link to reset your password is on its way.',
    backToSignIn: 'Back to sign in',
    resetTitle: 'Set your password',
    resetText: 'Choose a password for your account. You’ll use it to sign in.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    savePassword: 'Save password',
    savingPassword: 'Saving…',
    passwordsMismatch: 'The passwords don’t match.',
    passwordWeak: 'Choose a password that meets every requirement.',
    resetDone: 'Password saved. Sign in with your new password.',
    resetMissingToken: 'This link is incomplete. Ask for a new password reset link.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    rules: {
      length: 'At least 8 characters',
      lower: 'A lowercase letter',
      upper: 'An uppercase letter',
      number: 'A number',
      symbol: 'A symbol: @ $ ! % * ? &',
    },
    sessionExpired: 'Your session expired. Sign in again to continue.',
    verification: {
      title: 'Verify your email',
      checking: 'Verifying your email…',
      success: 'Your email is verified. Sign in to continue.',
      'invalid-token': 'This verification link is invalid or has expired.',
      'missing-token': 'This verification link is incomplete.',
      'server-error': 'We couldn’t verify your email. Please try the link again.',
    },
  },

  difficulty: {
    Easy: 'Easy',
    Medium: 'Medium',
    Hard: 'Hard',
  },

  verdicts: {
    Accepted: 'Accepted',
    'Wrong Answer': 'Wrong Answer',
    TLE: 'Time Limit Exceeded',
    'Time Limit Exceeded': 'Time Limit Exceeded',
    MLE: 'Memory Limit Exceeded',
    'Memory Limit Exceeded': 'Memory Limit Exceeded',
    'Runtime Error': 'Runtime Error',
    'Compilation Error': 'Compilation Error',
    'System Error': 'System Error',
    PENDING: 'Judging',
  },

  languages: {
    cpp: 'C++17',
    python: 'Python 3',
    javascript: 'JavaScript',
    java: 'Java',
  },

  problems: {
    title: 'Problems',
    listTitle: 'All problems',
    railLabel: 'Contests and streak',
    showAllTopics: 'Show all {count}',
    showFewerTopics: 'Show fewer',
    progress: {
      title: 'Your progress',
      ofTotal: 'of {total} solved',
    },
    solvedSummary: '{solved} of {total} solved',
    difficultyFilter: 'Difficulty',
    statusFilter: 'Status',
    any: 'All',
    statusAny: 'Any status',
    statusSolved: 'Solved',
    statusAttempted: 'Attempted',
    statusTodo: 'Not started',
    searchPlaceholder: 'Search problems',
    searchLabel: 'Search problems',
    topics: 'Topics',
    clearFilters: 'Clear filters',
    pickRandom: 'Pick one for me',
    pickRandomNone: 'Every problem in this view is solved.',
    count: { one: '{count} problem', other: '{count} problems' },
    columnStatus: 'Status',
    columnNumber: '#',
    columnTitle: 'Title',
    columnAcceptance: 'Acceptance',
    columnDifficulty: 'Difficulty',
    sortBy: 'Sort by {column}',
    keyboardHint: 'to move, Enter to open',
    emptyTitle: 'No problems match',
    emptyText: 'Try a different search or clear the filters.',
    noProblemsTitle: 'No problems yet',
    noProblemsText: 'Your institution hasn’t published any problems. Check back soon.',
    loadError: 'We couldn’t load problems.',
    acceptance: '{rate}%',
    today: {
      title: 'Today’s problem',
      solveNow: 'Solve now',
      resetsIn: 'Next problem in {time}',
      streakKeep: { one: 'Solve it to extend your {count}-day streak.', other: 'Solve it to extend your {count}-day streak.' },
      streakStart: 'Solve it to start a streak.',
      streakDone: { one: 'Done for today. You’re on a {count}-day streak.', other: 'Done for today. You’re on a {count}-day streak.' },
      reviewSolved: 'Review your solution',
      upNext: 'Up next',
      upNextHint: 'No daily problem today, so here’s your next unsolved one.',
      allSolved: 'You’ve solved every problem.',
      allSolvedHint: 'New problems from your institution will appear in the list below.',
      continueTitle: 'Continue',
      acceptance: '{rate}% acceptance',
    },
    streak: {
      title: 'Daily streak',
      current: 'Current',
      best: 'Best this month',
      days: { one: '{count} day', other: '{count} days' },
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      solvedDay: 'Solved: {title}',
      missedDay: 'Missed: {title}',
      todayDay: 'Today: {title}',
      upcomingDay: 'Daily problem: {title}',
    },
    contest: {
      live: 'Live now',
      upcoming: 'Next contest',
      register: 'Register',
      registered: 'Registered',
      enter: 'Enter contest',
      viewAll: 'All contests',
      startsInLabel: 'Starts in',
      endsInLabel: 'Ends in',
    },
  },

  workspace: {
    backToProblems: 'Problems',
    description: 'Description',
    submissions: 'Submissions',
    run: 'Run',
    submit: 'Submit',
    running: 'Running…',
    judging: 'Judging…',
    runWithCooldown: 'Run ({seconds}s)',
    submitWithCooldown: 'Submit ({seconds}s)',
    runShortcut: 'Run · {mod} Enter',
    submitShortcut: 'Submit · {mod} Shift Enter',
    example: 'Example {number}',
    input: 'Input',
    output: 'Output',
    explanation: 'Explanation',
    constraints: 'Constraints',
    topics: 'Topics',
    acceptance: '{rate}% acceptance',
    solved: 'Solved',
    noStatement: 'No statement is available for this problem.',
    notFound: 'This problem doesn’t exist or isn’t available.',
    acceptedToast: 'Accepted. Nice work!',
    mobileProblem: 'Problem',
    mobileCode: 'Code',
    mobileResult: 'Result',
    resizeHandle: 'Resize panels',
    editor: {
      language: 'Language',
      reset: 'Reset code',
      resetConfirm: 'Replace your code with the starter code for this language?',
      saved: 'Draft saved',
    },
    console: {
      testcases: 'Testcases',
      result: 'Result',
      case: 'Case {number}',
      addCase: 'Add test case',
      removeCase: 'Remove case {number}',
      restore: 'Restore samples',
      input: 'Input',
      expected: 'Expected output',
      yourOutput: 'Your output',
      error: 'Error',
      compilerOutput: 'Compiler output',
      empty: 'Run your code against the testcases, or submit it for judging.',
      runningSamples: 'Running your code…',
      judgingHidden: 'Judging against hidden tests…',
      passed: '{passed} of {total} testcases passed',
      hiddenAccepted: 'All hidden tests passed.',
      failedOn: 'Failed on hidden test {number}.',
      runtime: 'Runtime {value}s',
      memory: 'Memory {value} MB',
    },
    history: {
      emptyTitle: 'No submissions yet',
      emptyText: 'Submit a solution to see its verdict and code here.',
      count: { one: '{count} submission', other: '{count} submissions' },
      accepted: '{count} accepted',
      failedOn: 'Failed on hidden test {number}.',
    },
  },

  contests: {
    title: 'Contests',
    subtitle: 'Timed rounds with live standings, run by your institution.',
    past: 'Past contests',
    alsoUpcoming: 'Also coming up',
    endsAt: 'Ends at {time}',
    registrationCloses: 'Registration stays open until the contest ends.',
    register: 'Register',
    registered: 'Registered',
    leave: 'Leave contest',
    enter: 'Enter contest',
    standings: 'Standings',
    results: 'Results',
    details: 'Details',
    startsIn: 'Starts in {time}',
    endsIn: 'Ends in {time}',
    problemCount: { one: '{count} problem', other: '{count} problems' },
    pointCount: { one: '{count} point', other: '{count} points' },
    participantCount: { one: '{count} participant', other: '{count} participants' },
    registeredCount: { one: '{count} registered', other: '{count} registered' },
    noUpcomingTitle: 'No contests scheduled',
    noUpcomingText: 'New contests from your institution will appear here. You can register until each one starts.',
    noPastText: 'Contests you can review will appear here once they finish.',
    columnContest: 'Contest',
    columnDate: 'Date',
    columnParticipants: 'Participants',
    columnWinner: 'Winner',
    registeredToast: 'You’re registered for {name}.',
    leftToast: 'You left {name}.',
    loadError: 'We couldn’t load contests.',
    states: {
      draft: 'Draft',
      upcoming: 'Upcoming',
      live: 'Live',
      ended: 'Judging',
      finalized: 'Final',
      cancelled: 'Cancelled',
    },
  },

  contest: {
    back: 'Contests',
    startsInLabel: 'Starts in',
    endsInLabel: 'Ends in',
    starts: 'Starts',
    duration: 'Duration',
    penalty: 'Penalty',
    penaltyValue: '{minutes} min per wrong submission',
    registeredHint: 'You’re registered. The contest opens here when it starts.',
    notRegisteredHint: 'Register to take part. You can join until the contest ends.',
    lateHint: 'The contest has started, but you can still join. Time is counted from the start.',
    judgingHint: 'The contest is over. Final results appear once every submission is judged.',
    finalHint: 'Results are final. The problems are now open for practice.',
    cancelledHint: 'This contest was cancelled.',
    liveHint: 'The contest is running. Good luck!',
    problemsTitle: 'Problems',
    problemsLocked: 'Problems unlock when the contest starts.',
    problemsRegisterFirst: 'Only registered participants can see problems during the contest.',
    columnProblem: 'Problem',
    columnDifficulty: 'Difficulty',
    columnPoints: 'Points',
    columnStatus: 'Status',
    solved: 'Solved',
    attempted: 'Attempted',
    rulesTitle: 'Rules',
    rules: [
      'Problems unlock when the contest starts, and registration closes at the same moment.',
      'Your score is the sum of the points for every problem you solve.',
      'Ties are broken by finish time: the time of your last accepted submission, plus {penalty} minutes for each wrong submission on a problem you solved.',
      'Only submissions made before the contest ends count.',
    ],
    winnersTitle: 'Top finishers',
    notFound: 'This contest doesn’t exist or is no longer available.',
  },

  arena: {
    timeLeft: 'Time left',
    over: 'Ended',
    standings: 'Standings',
    rank: 'Rank {rank}',
    acceptedNote: 'Your standing updates at the next leaderboard refresh.',
    acceptedToast: '{label} accepted.',
    endedBanner: 'The contest has ended and submissions are closed.',
    viewOnly: 'You’re viewing this contest without registering, so you can’t submit.',
    gateUpcomingTitle: 'Starts in {time}',
    gateRegistered: 'You’re registered. This page opens automatically at the start.',
    gateNotRegistered: 'Register on the contest page to take part.',
    gateLateTitle: 'You’re not registered',
    gateLateText: 'The contest is running and you can still join. Register on the contest page, then come back here.',
    gateClosedTitle: 'Registration closed',
    gateClosedText: 'Only registered participants can enter. You can still follow the standings.',
    gateFinalTitle: 'This contest is over',
    gateFinalText: 'Final standings are published, and the problems are open for practice.',
    gateCancelledTitle: 'Contest cancelled',
    contestPage: 'Contest page',
  },

  standings: {
    title: 'Standings',
    live: 'Live · refreshes every minute',
    final: 'Final results',
    refresh: 'Refresh',
    refreshIn: 'Refresh in {seconds}s',
    updatedAt: 'Updated {time}',
    you: 'You',
    yourStanding: 'Your standing',
    notRanked: 'You’ll appear here after your first accepted submission.',
    columnRank: 'Rank',
    columnParticipant: 'Participant',
    columnScore: 'Score',
    columnFinish: 'Finish time',
    penaltyNote: '+{minutes} min penalty',
    wrongAttempts: { one: '{count} wrong submission', other: '{count} wrong submissions' },
    emptyTitle: 'No one is ranked yet',
    emptyText: 'Standings fill in as participants solve problems.',
    page: 'Page {page} of {pages}',
    unavailable: 'Standings open when the contest starts.',
  },

  tracker: {
    title: 'Tracker',
    intro: 'Solved problems across AlgoForge, LeetCode, Codeforces, and CodeChef. The list is ordered by that total.',
    columnRank: 'Rank',
    columnName: 'Name',
    columnAlgoForge: 'AlgoForge',
    columnLeetCode: 'LeetCode',
    columnCodeforces: 'Codeforces',
    columnCodeChef: 'CodeChef',
    columnTotal: 'Solved',
    loadError: 'We couldn’t load the tracker.',
    emptyTitle: 'No students yet',
    emptyText: 'Students in this organisation will show up here.',
  },

  courses: {
    title: 'Courses',
    intro: 'Problem sets put together for you.',
    loadError: 'We couldn’t load your courses.',
    emptyTitle: 'No courses yet',
    emptyText: 'When a course is assigned to you, it shows up here.',
    problemCount: { one: '{count} problem', other: '{count} problems' },
    progress: '{solved} of {total} solved',
    stateDone: 'Completed',
    stateActive: 'In progress',
    stateNew: 'Not started',
    back: 'All courses',
    eyebrow: 'Course',
    notFound: 'This course doesn’t exist or isn’t assigned to you.',
    start: 'Start course',
    continue: 'Continue',
    review: 'Review from the start',
    upNext: 'Up next',
    statSolved: 'Solved',
    statAttempted: 'Attempted',
    statTodo: 'To do',
    problemsTitle: 'Problems',
    problemsMeta: 'Only submissions made in this course count',
    backToCourse: 'Course',
    problemNotFound: 'This problem isn’t part of this course.',
    columnStep: 'Step',
    emptyProblemsTitle: 'No problems yet',
    emptyProblemsText: 'This course doesn’t have any problems you can open yet.',
  },

  profiles: {
    promptTitle: 'Link your coding profiles',
    sectionTitle: 'Linked profiles',
    intro: 'Paste your public profile links. We read how many problems you have solved on each site. You can skip this and add them later from your profile.',
    leetcode: 'LeetCode',
    codeforces: 'Codeforces',
    codechef: 'CodeChef',
    leetcodeHint: 'https://leetcode.com/u/your-name',
    codeforcesHint: 'https://codeforces.com/profile/your-handle',
    codechefHint: 'https://www.codechef.com/users/your-handle',
    save: 'Save links',
    skip: 'Skip for now',
    saved: 'Profile links saved. Counts are loading now. A saved profile stays locked unless an admin unlocks it.',
    linkProfile: 'Link profile',
    fieldLocked: 'Locked. Contact your admin to change this profile.',
    failed: 'Failed. This will be tried again at midnight.',
    invalid: 'Paste a public profile link for this site.',
    solvedLine: '{count} solved',
    notLinked: 'Not linked',
    pending: 'Waiting for the first refresh',
    checked: 'Last checked {time}',
  },

  profile: {
    title: 'Profile',
    factSolved: 'Solved',
    factSubmissions: 'Submissions',
    factAcceptance: 'Acceptance',
    factStreak: 'Streak',
    activity: 'Activity',
    activityLabel: 'Submissions per day over the past year',
    activityDay: { one: '{count} submission on {date}', other: '{count} submissions on {date}' },
    activeDays: { one: 'Active on {count} day', other: 'Active on {count} days' },
    less: 'Less',
    more: 'More',
    languages: 'Languages',
    languageCount: { one: '{count} submission · {accepted} accepted', other: '{count} submissions · {accepted} accepted' },
    solved: 'Solved problems',
    solvedOf: '{solved} / {total}',
    recent: 'Recent submissions',
    columnProblem: 'Problem',
    columnVerdict: 'Verdict',
    columnLanguage: 'Language',
    columnRuntime: 'Runtime',
    columnSubmitted: 'Submitted',
    emptyTitle: 'No submissions yet',
    emptyText: 'Solve a problem and your submissions will show up here.',
    loadError: 'We couldn’t load your profile.',
    startPractising: 'Start practising',
  },

  blog: {
    title: 'Blog',
    subtitle: 'Explanations, approaches and contest write-ups from your community.',
    write: 'Write a post',
    writeFirst: 'Write the first post',
    continueDraft: 'Continue your draft',
    backToBlog: 'Blog',
    readingTime: { one: '{count} min read', other: '{count} min read' },
    formerMember: 'Former member',
    loadError: 'We couldn’t load the blog.',
    emptyTitle: 'No posts yet',
    emptyText: 'Share how you cracked a problem or what you learned in a contest. Every post is reviewed before it goes live.',
    pageEmpty: 'There are no posts on this page.',
    firstPage: 'Go to the first page',
    page: 'Page {page} of {pages}',
    delete: 'Delete',
    deleteConfirm: 'Delete post',
    deleted: 'Post deleted.',
    tabs: {
      all: 'All posts',
      mine: 'My posts',
    },
    status: {
      draft: 'Draft',
      pending: 'In review',
      rejected: 'Changes requested',
      published: 'Published',
      unpublished: 'Unpublished',
    },
    mine: {
      untitled: 'Untitled draft',
      draftMeta: 'Saved on this device {time} · {minutes} min read',
      continue: 'Continue writing',
      discard: 'Discard',
      discardConfirm: 'Discard draft',
      submittedAgo: 'Submitted {time}',
      publishedAgo: 'Published {time}',
      adminNote: 'Note from your admin',
      edit: 'Edit',
      editResubmit: 'Edit and resubmit',
      quota: '{count} of {max} in review',
      quotaHint: 'Posts in review or sent back for changes count toward your limit of {max}.',
      quotaLabel: 'Posts in review',
      emptyTitle: 'You haven’t written anything yet',
      emptyText: 'Your submitted posts and their review status will appear here.',
    },
    notice: {
      pending: 'This post is waiting for review. An admin will publish it or send it back with feedback. It can’t be edited while it’s in review.',
      rejectedTitle: 'Changes requested',
      unpublishedTitle: 'An admin unpublished this post',
    },
    reader: {
      onThisPage: 'On this page',
      morePosts: 'More from the blog',
      copyLink: 'Copy link',
      linkCopied: 'Link copied.',
      linkCopyFailed: 'Couldn’t copy the link.',
      notFound: 'This post doesn’t exist or isn’t available.',
      code: 'Code',
      blockedImage: 'This image can’t be shown. Add images with the image button in the editor.',
      blockedImageNamed: '“{alt}” can’t be shown. Add images with the image button in the editor.',
    },
    editor: {
      newTitle: 'New post',
      editTitle: 'Edit post',
      backToPost: 'Back to post',
      titleLabel: 'Title',
      titlePlaceholder: 'Give your post a title',
      summaryLabel: 'Summary',
      summaryPlaceholder: 'Add a one-line summary for the post card (optional)',
      bodyLabel: 'Post',
      bodyPlaceholder: 'Write your post in Markdown.\n\n## Intuition\nWhat makes this problem click?\n\n```cpp\n// your solution\n```',
      addCover: 'Add a cover image',
      coverHint: 'Drop an image here or click to choose. JPEG, PNG, WebP or GIF.',
      replaceCover: 'Replace',
      removeCover: 'Remove cover image',
      imageAlt: 'image',
      imageCount: '{count}/{max} images',
      count: '{count} / {max}',
      hint: 'Markdown supported. Drafts are saved on this device only, so finish on the same browser.',
      write: 'Write',
      preview: 'Preview',
      previewEmpty: 'Your post preview will appear here as you write.',
      view: 'Editor view',
      localOnly: 'Drafts save on this device',
      saving: 'Saving…',
      saved: 'Saved on this device',
      saveFailed: 'Couldn’t save the draft',
      notSaving: 'Draft isn’t being saved',
      storageUnavailable: 'This browser can’t save drafts (for example in a private window). Keep this tab open until you submit.',
      restored: 'Restored your unsaved changes to this post.',
      discardDraft: 'Discard draft',
      discardChanges: 'Discard changes',
      discardConfirm: 'Discard',
      draftDiscarded: 'Draft discarded.',
      changesDiscarded: 'Changes discarded.',
      submit: 'Submit for review',
      resubmit: 'Resubmit for review',
      publish: 'Publish',
      saveChanges: 'Save changes',
      uploading: 'Uploading {percent}%',
      finishing: 'Finishing…',
      viewPost: 'View post',
      lockedPending: 'This post is in review and can’t be edited right now.',
      lockedPublished: 'Published posts can’t be edited.',
      reviewLimitNotice: 'You already have {max} posts in review or waiting for changes. Keep writing; your draft is saved on this device. You can submit once one of them is reviewed.',
      imageLimit: 'A post can contain up to {max} images.',
      imageLimitPartial: { one: 'Only {count} more image fits in this post.', other: 'Only {count} more images fit in this post.' },
      imageErrors: {
        type: 'Use a JPEG, PNG, WebP or GIF image.',
        size: 'That image is too large. Images must be under 2 MB after compression.',
        decode: 'That image couldn’t be read. Try a different file.',
      },
      problems: {
        title: 'Add a title to submit.',
        titleLong: 'Titles can be at most {max} characters.',
        summaryLong: 'Summaries can be at most {max} characters.',
        body: 'Write your post to submit.',
        bodyLong: 'Posts can be up to {max} characters. Split long posts into a series.',
        images: 'A post can contain up to {max} images.',
        missingImages: 'Some images in this draft are missing. Remove them and add them again.',
        foreignImages: 'Images from other sites aren’t allowed. Add images with the image button.',
        processing: 'Wait for your images to finish processing.',
        reviewLimit: 'You have {max} posts in review already.',
      },
      tools: {
        label: 'Formatting',
        heading: 'Heading',
        subheading: 'Subheading',
        bold: 'Bold',
        italic: 'Italic',
        link: 'Link',
        inlineCode: 'Inline code',
        codeBlock: 'Code block',
        quote: 'Quote',
        bulletList: 'Bulleted list',
        numberedList: 'Numbered list',
        image: 'Image',
      },
      placeholders: {
        bold: 'bold text',
        italic: 'italic text',
      },
      done: {
        submitted: 'Submitted for review. You’ll see the decision in My posts.',
        resubmitted: 'Resubmitted for review.',
        published: 'Published.',
        saved: 'Changes saved.',
      },
    },
  },
};

export default defaultStrings;
